/**
 * Optimistic UI State Types
 *
 * These interfaces support instant UI feedback before BC (Business Central)
 * confirmation. They extend existing models with optimistic metadata.
 *
 * Usage:
 * - When a user initiates a planning action, create an optimistic item immediately
 * - Display with 'pending' state and visual indicators (striped background, etc.)
 * - Once BC confirms/fails via SignalR, update state accordingly
 */

import { PlanningBlock } from '../planning-block/planning-block.interface';
import { TransportOrder } from '../transport-order/transport-order.interface';

/**
 * Possible states for optimistic items
 */
export type OptimisticStatus = 'pending' | 'confirmed' | 'failed';

/**
 * Constants for OptimisticStatus values (for easy comparison)
 */
export const OptimisticStatus = {
  Pending: 'pending' as OptimisticStatus,
  Confirmed: 'confirmed' as OptimisticStatus,
  Failed: 'failed' as OptimisticStatus,
} as const;

/**
 * Base interface for all optimistic items
 */
export interface OptimisticMetadata {
  /** Current state of the optimistic operation */
  optimisticState: OptimisticStatus;

  /** Client-generated temporary ID (before BC assigns real ID) */
  optimisticId: string;

  /** When the optimistic operation started (for timeout detection) */
  pendingSince: Date;

  /** Correlation ID for tracking async operation through system */
  correlationId: string;

  /** User who initiated the action (for multi-user awareness) */
  initiatedByUserId?: string;

  /** Error message if optimisticState is 'failed' */
  errorMessage?: string;

  /** Error code for programmatic handling */
  errorCode?: string;

  /** Whether the operation can be retried */
  canRetry?: boolean;
}

/**
 * Planning Block with optimistic metadata
 * Used for instant UI feedback when planning PBs
 */
export interface OptimisticPlanningBlock extends PlanningBlock, OptimisticMetadata {
  /** Real PB ID assigned by BC after confirmation */
  serverPbId?: number;
}

/**
 * Transport Order with optimistic metadata
 * Used for instant UI feedback when creating/updating TOs
 */
export interface OptimisticTransportOrder extends TransportOrder, OptimisticMetadata {
  /** Real TO ID assigned by BC after confirmation */
  serverToId?: string;

  /** Number of pending operations on this TO */
  pendingOperations?: number;
}

/**
 * Request to initiate optimistic planning
 */
export interface OptimisticPlanRequest {
  /** Planning blocks to plan */
  planningBlockIds: number[];

  /** Target capacity slot (truck/timeslot) */
  capacitySlotId: number;

  /** Driver ID */
  driverId?: string;

  /** Co-driver ID */
  coDriverId?: string;

  /** Haulier ID */
  haulierId?: string;

  /** Planning group */
  planningGroupId?: string;

  /** Planned date/time from */
  plannedTimeFrom?: Date;

  /** Planned date/time until */
  plannedTimeUntil?: Date;

  /** Optional: existing TO to plan to */
  existingTransportOrderId?: string;

  /** Optional: additional planning options */
  options?: {
    autoGroup?: boolean;
    autoSend?: boolean;
    skipConflictCheck?: boolean;
  };
}

/**
 * Response from server when initiating optimistic planning
 */
export interface OptimisticPlanResponse {
  /** Correlation ID for tracking */
  correlationId: string;

  /** Temporary TO ID to use until BC confirms */
  optimisticTOId: string;

  /** Estimated completion time */
  estimatedCompletion?: Date;

  /** Status - should be 202 Accepted */
  accepted: boolean;

  /** Validation errors if not accepted */
  validationErrors?: string[];
}

/**
 * Payload sent via SignalR when planning starts (for multi-user sync)
 */
export interface PlanningStartedPayload {
  correlationId: string;
  optimisticTOId: string;
  optimisticPbIds: string[];
  capacitySlotId: number;
  plannedTimeFrom: Date;
  plannedTimeUntil: Date;
  initiatedByUserId: string;
  planningGroup: string;
}

/**
 * Payload sent via SignalR when BC confirms planning
 */
export interface PlanningConfirmedPayload {
  correlationId: string;

  /** The confirmed Transport Order from BC */
  transportOrder: TransportOrder;

  /** The confirmed Planning Blocks from BC */
  planningBlocks: PlanningBlock[];

  /** Calculated values from BC */
  calculatedValues: {
    distances?: number[];
    costs?: { total: number; breakdown: Record<string, number> };
    etas?: { actionId: string; eta: Date }[];
    conflicts?: { type: string; message: string; severity: 'warning' | 'error' }[];
  };
}

/**
 * Payload sent via SignalR when planning fails
 */
export interface PlanningFailedPayload {
  correlationId: string;
  optimisticTOId: string;
  optimisticPbIds: string[];
  errorCode: string;
  errorMessage: string;
  canRetry: boolean;
  /** Specific errors per PB if partial failure */
  pbErrors?: { pbId: number; error: string }[];
}

/**
 * State slice for managing optimistic items in Redux store
 */
export interface OptimisticState {
  /** Map of pending optimistic plans by correlationId */
  pendingPlans: Map<string, OptimisticPlanningBlock[]>;

  /** Map of pending optimistic TOs by correlationId */
  pendingTransportOrders: Map<string, OptimisticTransportOrder>;

  /** Recent confirmations (for UI feedback, cleared after timeout) */
  recentConfirmations: PlanningConfirmedPayload[];

  /** Recent failures (for UI feedback, cleared after dismiss) */
  recentFailures: PlanningFailedPayload[];

  /** Global count of pending operations */
  pendingCount: number;
}

/**
 * Helper to check if a planning block is optimistic
 */
export function isOptimisticPlanningBlock(pb: PlanningBlock): pb is OptimisticPlanningBlock {
  return 'optimisticState' in pb && 'optimisticId' in pb;
}

/**
 * Helper to check if a transport order is optimistic
 */
export function isOptimisticTransportOrder(to: TransportOrder): to is OptimisticTransportOrder {
  return 'optimisticState' in to && 'optimisticId' in to;
}

/**
 * Generate a unique optimistic ID
 */
export function generateOptimisticId(prefix: 'PB' | 'TO' = 'TO'): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `OPT-${prefix}-${timestamp}-${random}`;
}

/**
 * Generate a correlation ID for tracking async operations
 */
export function generateCorrelationId(): string {
  return `COR-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 10)}`;
}

/**
 * Default timeout for optimistic operations (30 seconds)
 */
export const OPTIMISTIC_TIMEOUT_MS = 30000;

/**
 * Check if an optimistic item has timed out
 */
export function isOptimisticTimedOut(metadata: OptimisticMetadata): boolean {
  if (metadata.optimisticState !== 'pending') return false;
  const elapsed = Date.now() - metadata.pendingSince.getTime();
  return elapsed > OPTIMISTIC_TIMEOUT_MS;
}
